import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveModeModel } from '../hooks/useLiveModeModel';
import { TERM } from '../liveModeMeta';
import { StatusBar } from './StatusBar';
import { FurnaceHero } from './FurnaceHero';
import { AlertRail } from './AlertRail';
import { RecommendationTicker } from './RecommendationTicker';
import { PerformanceStats } from './PerformanceStats';

// Presentation-layer keyframes. Each is gated per-element by the reduced-motion
// `animate` flag. Motion only ever means "this just changed".
const KEYFRAMES = `
@keyframes lm-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes lm-blink { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }
@keyframes lm-fade-in { from { opacity: 0; } to { opacity: 1; } }
`;

/**
 * The operator control-room screen — a committed dark "terminal": one instrument
 * frame, hairline-ruled panes, monospace readouts. Its job is the glance answer
 * ("does the AI need me, and what changed?"), not operating the furnace.
 */
export function LiveModeScreen({ deploymentName, svgUrl, seedAlerts, seedRecs, isLoading, isError, onSelectRec, onSelectAlert }) {
  const { isDesktop } = useBreakpoint();
  const {
    alerts, recs, clock, flashId, jitterActive,
    degraded, rankedRecs, rankedAlerts,
    headline: headlineOut, freshness, animate,
  } = useLiveModeModel(seedAlerts, seedRecs);

  // Panes are borderless; a 1px grid gap over a line-coloured backdrop draws the
  // hairline rules between them (the multi-pane terminal look).
  const cell = (col, row) => ({
    minWidth: 0, minHeight: 0, display: 'flex',
    ...(isDesktop ? { gridColumn: col, gridRow: row } : {}),
  });

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
      background: TERM.void, color: TERM.ink, fontFamily: typography.fontFamily.body,
    }}>
      <style>{KEYFRAMES}</style>

      <div style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        border: `1px solid ${TERM.lineBright}`, overflow: 'hidden',
      }}>
        <StatusBar
          deploymentName={deploymentName}
          clock={clock}
          jitterActive={jitterActive}
          animate={animate}
          headline={headlineOut}
          freshness={freshness}
        />

        {isError ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: TERM.panel }}>
            <ErrorState message="SIGNAL LOST — couldn't load live data for this deployment." />
          </div>
        ) : isLoading && alerts.length === 0 && recs.length === 0 ? (
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isDesktop ? '1.85fr 1fr' : '1fr', gap: 1, background: TERM.line }}>
            <div style={{ background: TERM.panel, padding: 14 }}><SkeletonCard rows={6} height="100%" /></div>
            <div style={{ background: TERM.panel, padding: 14 }}><SkeletonCard rows={6} height="100%" /></div>
          </div>
        ) : (
          <div style={{
            flex: 1, minHeight: 0,
            display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
            gridTemplateColumns: isDesktop ? '1.85fr 1fr' : undefined,
            gridTemplateRows: isDesktop ? '3fr 2fr' : undefined,
            gap: 1, background: TERM.line,
          }}>
            <div style={cell(1, 1)}><RecommendationTicker recs={rankedRecs} paused={degraded} animate={animate} onSelect={onSelectRec} /></div>
            <div style={cell(1, 2)}><AlertRail alerts={rankedAlerts} flashId={flashId} animate={animate} onSelect={onSelectAlert} /></div>
            <div style={cell(2, 1)}><FurnaceHero svgUrl={svgUrl} params={rankedAlerts} /></div>
            <div style={cell(2, 2)}><PerformanceStats alerts={alerts} recs={recs} /></div>
          </div>
        )}
      </div>
    </div>
  );
}
